<template>
  <div class="centered-container feedback-page">
    <h2 class="sub-heading">Feedback admin</h2>

    <div v-if="!unlocked" class="content-box">
      <div class="box-heading">
        <h3>Unlock</h3>
      </div>
      <div class="box-content">
        <p class="hint">Enter the admin passphrase. It is not stored in the page source.</p>
        <div class="input-container">
          <input
            v-model="passphrase"
            type="password"
            autocomplete="current-password"
            placeholder="Passphrase"
            @keyup.enter="unlock"
          />
          <button type="button" :disabled="unlocking || !passphrase" @click="unlock">
            {{ unlocking ? "Checking…" : "Unlock" }}
          </button>
        </div>
        <p v-if="error" class="error">{{ error }}</p>
      </div>
    </div>

    <template v-else>
      <div class="filters">
        <label>
          Type
          <select v-model="typeFilter">
            <option value="all">All</option>
            <option value="feature">Feature</option>
            <option value="bug">Bug</option>
            <option value="general">General</option>
          </select>
        </label>
        <label>
          Status
          <select v-model="statusFilter">
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="implemented">Implemented</option>
            <option value="duplicate">Duplicate</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <label>
          Sort
          <select v-model="sortBy">
            <option value="newest">Newest</option>
            <option value="plus">Most voted</option>
          </select>
        </label>
      </div>

      <p v-if="loading" class="hint">Loading…</p>
      <p v-else-if="!visibleItems.length" class="hint">No items match these filters.</p>
      <p v-if="error" class="error">{{ error }}</p>
      <p v-if="savedId" class="thanks">{{ savedId }}</p>

      <div v-for="item in visibleItems" :key="item.id" class="content-box">
        <div class="box-heading">
          <h3>{{ item.title }}</h3>
        </div>
        <div class="box-content item-body">
          <p class="meta">
            <span class="chip">{{ item.type }}</span>
            <span v-if="item.type === 'feature'" class="chip plus-chip">
              <span>+1 votes</span>
              <span class="plus-count">{{ item.plusCount || 0 }}</span>
            </span>
            <span class="chip muted">{{ item.id }}</span>
          </p>
          <p v-if="item.details" class="details">{{ item.details }}</p>
          <p v-if="item.contact" class="contact">Contact: {{ item.contact }}</p>
          <p v-else class="hint">No contact given.</p>

          <label class="field-label">Status</label>
          <select v-model="item.status">
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="implemented">Implemented</option>
            <option value="duplicate">Duplicate</option>
            <option value="rejected">Rejected</option>
          </select>

          <label v-if="item.status === 'duplicate'" class="field-label">Duplicate of (id)</label>
          <input
            v-if="item.status === 'duplicate'"
            v-model="item.duplicateOf"
            type="text"
            class="form-input"
            placeholder="Other item id"
          />

          <label class="field-label">Internal note</label>
          <textarea v-model="item.adminNote" class="form-textarea" rows="3"></textarea>

          <div class="item-actions">
            <button
              type="button"
              class="save-button"
              :disabled="savingId === item.id || deletingId === item.id"
              @click="saveItem(item)"
            >
              {{ savingId === item.id ? "Saving…" : "Save" }}
            </button>
            <button
              type="button"
              class="delete-button"
              :disabled="savingId === item.id || deletingId === item.id"
              @click="askDelete(item)"
            >
              {{ deletingId === item.id ? "Deleting…" : "Delete" }}
            </button>
          </div>
        </div>
      </div>

      <ModalDialog
        :show="!!pendingDelete"
        title="Delete this item?"
        :message="deleteMessage"
        confirm-text="Delete"
        @confirm="deleteItem"
        @cancel="pendingDelete = null"
      />
    </template>
  </div>
</template>

<script>
import ModalDialog from "@/components/Modal.vue";
import { feedbackRequest, getAdminKey, setAdminKey } from "@/utils/feedback";

export default {
  name: "FeedbackAdmin",
  components: { ModalDialog },
  data() {
    return {
      passphrase: "",
      unlocking: false,
      unlocked: false,
      loading: false,
      error: "",
      items: [],
      typeFilter: "all",
      statusFilter: "all",
      sortBy: "newest",
      savingId: null,
      savedId: "",
      pendingDelete: null,
      deletingId: null
    };
  },
  computed: {
    visibleItems() {
      const filtered = this.items.filter((item) => {
        if (this.typeFilter !== "all" && item.type !== this.typeFilter) return false;
        if (this.statusFilter !== "all" && item.status !== this.statusFilter) return false;
        return true;
      });
      return filtered.slice().sort((a, b) => {
        if (this.sortBy === "plus") {
          const plus = (b.plusCount || 0) - (a.plusCount || 0);
          if (plus !== 0) return plus;
        }
        return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
      });
    },
    deleteMessage() {
      const item = this.pendingDelete;
      if (!item) return "";
      return `"${item.title}" will be removed for good, including any +1s. This cannot be undone.`;
    }
  },
  async mounted() {
    const existing = getAdminKey();
    if (existing) {
      this.passphrase = existing;
      await this.unlock();
    }
  },
  methods: {
    async unlock() {
      this.unlocking = true;
      this.error = "";
      try {
        await feedbackRequest("POST", "feedback/admin/session", {
          body: { key: this.passphrase }
        });
        setAdminKey(this.passphrase);
        this.unlocked = true;
        await this.loadItems();
      } catch (err) {
        setAdminKey("");
        this.unlocked = false;
        this.error = err.status === 401 ? "Wrong passphrase." : err.message;
      } finally {
        this.unlocking = false;
      }
    },
    async loadItems() {
      this.loading = true;
      this.error = "";
      try {
        const data = await feedbackRequest("GET", "feedback/admin", {
          adminKey: getAdminKey()
        });
        this.items = (data.items || []).map((item) => ({
          ...item,
          duplicateOf: item.duplicateOf || "",
          adminNote: item.adminNote || ""
        }));
      } catch (err) {
        if (err.status === 401) {
          setAdminKey("");
          this.unlocked = false;
        }
        this.error = err.message || "Could not load items.";
      } finally {
        this.loading = false;
      }
    },
    async saveItem(item) {
      this.savingId = item.id;
      this.error = "";
      this.savedId = "";
      try {
        const updated = await feedbackRequest("POST", `feedback/admin/${item.id}`, {
          adminKey: getAdminKey(),
          body: {
            status: item.status,
            duplicateOf: item.status === "duplicate" ? item.duplicateOf : "",
            adminNote: item.adminNote
          }
        });
        const index = this.items.findIndex((row) => row.id === item.id);
        if (index !== -1) {
          this.items.splice(index, 1, {
            ...updated,
            duplicateOf: updated.duplicateOf || "",
            adminNote: updated.adminNote || ""
          });
        }
        this.savedId = `Saved ${item.id}.`;
      } catch (err) {
        if (err.status === 401) {
          setAdminKey("");
          this.unlocked = false;
        }
        this.error = err.message || "Could not save.";
      } finally {
        this.savingId = null;
      }
    },
    askDelete(item) {
      this.pendingDelete = item;
      this.error = "";
      this.savedId = "";
    },
    async deleteItem() {
      const item = this.pendingDelete;
      if (!item) return;
      this.deletingId = item.id;
      this.pendingDelete = null;
      this.error = "";
      this.savedId = "";
      try {
        await feedbackRequest("POST", `feedback/admin/${item.id}`, {
          adminKey: getAdminKey(),
          body: { delete: true }
        });
        this.items = this.items.filter((row) => row.id !== item.id);
        this.savedId = `Deleted "${item.title}".`;
      } catch (err) {
        if (err.status === 401) {
          setAdminKey("");
          this.unlocked = false;
        }
        this.error = err.message || "Could not delete.";
      } finally {
        this.deletingId = null;
      }
    }
  }
};
</script>

<style scoped>
.hint {
  margin-top: 10px;
  color: #666;
  line-height: 1.4;
}

.thanks {
  margin-top: 12px;
  color: #1e8449;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  text-align: left;
}

.filters label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}

.filters select,
.item-body select,
.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid #ccc;
  font-family: inherit;
  font-size: 14px;
}

.item-body {
  text-align: left;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.chip {
  background-color: #3498db;
  color: #fff;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 12px;
  text-transform: capitalize;
}

.chip.muted {
  background-color: #eee;
  color: #333;
  text-transform: none;
}

.plus-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: none;
  padding: 4px 4px 4px 8px;
}

.plus-count {
  min-width: 1.5em;
  padding: 2px 6px;
  border-radius: 3px;
  background-color: rgba(255, 255, 255, 0.25);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.details {
  line-height: 1.4;
  white-space: pre-wrap;
  margin-bottom: 10px;
}

.contact {
  line-height: 1.4;
  margin-bottom: 8px;
}

.field-label {
  display: block;
  font-size: 13px;
  margin: 12px 0 6px;
}

.form-textarea {
  min-height: 72px;
  resize: vertical;
}

.item-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.save-button,
.delete-button {
  margin: 0;
}

.delete-button {
  background-color: #c0392b;
}

.delete-button:hover {
  background-color: #a93226;
}

.input-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.input-container input {
  flex: 1;
}

.input-container button {
  margin-left: 0;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .input-container {
    flex-direction: column;
    align-items: stretch;
  }

  .input-container button,
  .save-button,
  .delete-button,
  .filters select {
    min-height: 44px;
  }
}
</style>
